import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import type { User } from '@supabase/supabase-js';
import { useInactivityLogout } from '../hooks/useInactivityLogout';
import { isDeviceTrusted, trustCurrentDevice, getRememberDeviceFlag, clearRememberDeviceFlag, isSessionVerified, markSessionVerified, clearSessionVerified } from '../lib/trustedDevices';
import { clearSelectedBusiness } from '../lib/storage';

type MfaStatus = 'loading' | 'enroll_needed' | 'challenge_needed' | 'verified' | 'none';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  mfaStatus: MfaStatus;
  signOut: () => Promise<void>;
  refreshMfaStatus: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

async function determineMfaStatus(user: User | null): Promise<MfaStatus> {
  if (!user) return 'none';

  const { data: aalData, error: aalError } =
    await supabase.auth.mfa.getAuthenticatorAssuranceLevel();

  if (aalError || !aalData) return 'none';

  const { currentLevel, nextLevel } = aalData;

  if (currentLevel === 'aal2') return 'verified';

  const { data: factorsData } = await supabase.auth.mfa.listFactors();
  const hasVerifiedFactor = factorsData?.totp?.some((f) => f.status === 'verified');

  if (nextLevel === 'aal2' || hasVerifiedFactor) {
    if (isSessionVerified(user.id)) {
      return 'verified';
    }
    const trusted = await isDeviceTrusted(user.id);
    if (trusted) {
      return 'verified';
    }
    return 'challenge_needed';
  }

  return 'enroll_needed';
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [mfaStatus, setMfaStatus] = useState<MfaStatus>('loading');

  const signOut = useCallback(async () => {
    clearSessionVerified();
    await clearSelectedBusiness();
    await supabase.auth.signOut();
    setUser(null);
    setMfaStatus('none');
  }, []);

  const sessionStillValid = !!user && isSessionVerified(user.id);
  useInactivityLogout(signOut, !!user && mfaStatus === 'verified' && !sessionStillValid);

  const refreshMfaStatus = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const currentUser = session?.user ?? null;
    if (currentUser) setUser(currentUser);
    const status = await determineMfaStatus(currentUser);
    setMfaStatus(status);

    if (status === 'verified' && currentUser) {
      markSessionVerified(currentUser.id);
      if (getRememberDeviceFlag()) {
        clearRememberDeviceFlag();
        trustCurrentDevice(currentUser.id).catch(() => {});
      }
    }
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      const currentUser = session?.user ?? null;
      setUser(currentUser);
      const status = await determineMfaStatus(currentUser);
      setMfaStatus(status);
      if (status === 'verified' && currentUser) {
        markSessionVerified(currentUser.id);
      }
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const currentUser = session?.user ?? null;
      setUser(currentUser);
      if (!currentUser) {
        setMfaStatus('none');
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        mfaStatus,
        signOut,
        refreshMfaStatus,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
